import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import { noop } from 'lodash';
import { ComponentType, ReactNode } from 'react';

import { IconProps } from '@/components/icons/ChevronDownIcon';
import { ClockFastForwardIcon } from '@/components/icons/ClockFastForwardIcon';
import { Card } from '@/components/layout/Card/Card';
import { AIAssistBadge } from '@/components/layout/DeliverableCard/AIAssistBadge';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { COLORS } from '@/styles/tokens/colors';

export interface DeliverableCardProps {
  title: React.ReactNode;
  description?: string;
  /**
   * Custom node rendered in place of the `description` string. Use when the
   * description needs its own styling (e.g. colored status text with an icon).
   * Takes precedence over `description` when both are provided.
   */
  descriptionContent?: ReactNode;
  icon?: ComponentType<IconProps>;
  iconProps?: Omit<IconProps, 'height' | 'width'>;
  timeEstimateLabel?: string;
  showTimeEstimate?: boolean;
  sx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  sizeVariant: 'large' | 'small';
  onClick?: () => void;
  badgeComponent?: ReactNode;
  hideBadge?: boolean;
  dataTestId?: string;
  maxLines?: number;
}

export function DeliverableCard({
  title,
  description,
  descriptionContent,
  icon: Icon,
  iconProps,
  sx,
  contentSx,
  timeEstimateLabel,
  showTimeEstimate = false,
  sizeVariant,
  onClick = noop,
  badgeComponent,
  hideBadge,
  dataTestId,
  maxLines,
}: DeliverableCardProps) {
  const [isHovered, props] = useIsElementHovered();

  const timeEstimate =
    timeEstimateLabel && showTimeEstimate ? (
      <Stack direction="row" alignItems="center" gap={0.5}>
        <ClockFastForwardIcon
          size={14}
          color={COLORS.BLUE[500]}
          strokeWidth={1.33}
        />
        <Typography
          variant="title-xs"
          component="span"
          color={COLORS.TEXT.SECONDARY}
        >
          {timeEstimateLabel}
        </Typography>
      </Stack>
    ) : null;

  return (
    <Card
      data-testid={dataTestId || 'DeliverableCard'}
      variant="outlined"
      component="button"
      sx={{
        p: 2,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
        cursor: 'pointer',
        textAlign: 'left',
        borderColor: isHovered ? COLORS.NAVY[700] : undefined,
        ':focus-visible': {
          outline: 'solid black 2px',
          outlineOffset: 2,
        },
        ...sx,
      }}
      {...props}
      onClick={onClick}
    >
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Box display="flex" alignItems="center" gap={2}>
            {Icon && (
              <Icon
                size={sizeVariant === 'small' ? 24 : 36}
                color={COLORS.GRAY[300]}
                {...iconProps}
                sx={{
                  '& svg path': {
                    strokeWidth: 1.33,
                  },
                  ...iconProps?.sx,
                }}
              />
            )}
          </Box>
          {timeEstimate}
        </Stack>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          flexGrow: 1,
          ...contentSx,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              mt: sizeVariant === 'large' ? 2 : 1,
            }}
          >
            <Typography
              variant={sizeVariant === 'large' ? 'title-l' : 'title-s'}
            >
              {title}
            </Typography>
          </Box>
          {descriptionContent
            ? descriptionContent
            : description && (
                <TypographyClamped
                  variant="body-s"
                  color={COLORS.TEXT.SECONDARY}
                  lines={maxLines}
                  // Reserve space to maintain consistent heights in grid layouts
                  reserveSpace={true}
                >
                  {description}
                </TypographyClamped>
              )}
        </Box>
      </Box>
      {sizeVariant === 'large' && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            mt: 2,
            minHeight: 24,
          }}
        >
          {!hideBadge && (badgeComponent || <AIAssistBadge />)}
        </Box>
      )}
    </Card>
  );
}
